import type { ReactNode } from "react";
import clsx from "clsx";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import styles from "./Spotlight.module.scss";

export interface SpotlightTile {
  /** The uppercased mono label (e.g. "Total followers"). */
  label: string;
  /** The tile value. `null`/`undefined` renders `—` — NEVER a fabricated number (born-blank / leak-test).*/
  value: ReactNode;
  /** The compact value variant (baseline row) — the "top mover" tile that pairs a platform name with a delta. */
  small?: boolean;
}

export interface SpotlightProps {
  /** Small mono eyebrow. Takes a node so callers can lead with a real Lucide glyph
   * (brand guide §12 — never a text glyph like ★). Omit it entirely for an eyebrow-less hero. */
  eyebrow?: ReactNode;
  /** The display headline. */
  title: string;
  /** One-line supporting copy. */
  subtitle?: string;
  /**
   * Signed percent change → the up/down delta pill (top-right). `null`/`undefined` → no pill (born-blank hides it;
   * never a fabricated 0%). Ignored while {@link SpotlightProps.action} is rendered.
   */
  delta?: number | null;
  /** Rendered in the top-right when there is no {@link SpotlightProps.delta} (e.g. a retry affordance on error). */
  action?: ReactNode;
  /** The stat tiles (the dashboard hero ships three). */
  tiles: SpotlightTile[];
  /** Loading flash — swaps the headline + tile values for shimmer placeholders. */
  loading?: boolean;
  /**
   * A band press photo to sit behind the hero. When set, the photo fills the card under a readability
   * scrim and REPLACES the composed gradient; when absent the card keeps its gradient-only look — an
   * honest fallback, never a grey placeholder box.
   */
  photoUrl?: string | null;
  className?: string;
}

/**
 * The dark "spotlight" hero (first shipped by the Dashboard) — a near-black inverse card with a
 * composed radial+linear gradient, an eyebrow / display title / subtitle, an optional up/down delta pill, and a row
 * of stat tiles. PURE + presentational: the consuming screen derives the copy and figures and owns the null → `—`
 * contract at the tile boundary. Reused by Releases / Music / Content (FE-SH-02) — build once, consume four times.
 */
export function Spotlight({
  eyebrow,
  title,
  subtitle,
  delta,
  action,
  tiles,
  loading = false,
  photoUrl,
  className,
}: SpotlightProps) {
  const hasDelta = !loading && delta != null && Number.isFinite(delta);
  // The image is composed here, not in SCSS: the photo URL is per-band DATA, not a design token, so it must
  // not masquerade as one (the token firewall rightly rejects a phantom `--ac-*`). The scrim above it still
  // resolves from the registry. encodeURI keeps quotes/parens in a signed media URL inside the url().
  const photo = photoUrl
    ? { backgroundImage: `var(--ac-gradient-spotlight-scrim), url("${encodeURI(photoUrl)}")` }
    : undefined;
  return (
    <article
      className={clsx(styles.spotlight, photo && styles.hasPhoto, className)}
      style={photo}
    >
      <div className={styles.bg} aria-hidden="true" />
      <header className={styles.head}>
        <div className={styles.headMain}>
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          {loading ? (
            <span className={styles.skelTitle} aria-hidden="true" />
          ) : (
            <h2 className={styles.title}>{title}</h2>
          )}
          {subtitle && !loading && <p className={styles.sub}>{subtitle}</p>}
        </div>
        {hasDelta ? <DeltaPill value={delta!} /> : action}
      </header>

      <div className={styles.stats}>
        {tiles.map((tile, i) => (
          <div key={`${tile.label}-${i}`} className={styles.tile}>
            <p className={styles.tileLabel}>{tile.label}</p>
            {loading ? (
              <span className={styles.skelValue} aria-hidden="true" />
            ) : (
              <p className={clsx(styles.tileValue, tile.small && styles.tileValueSm)}>
                {tile.value == null ? "—" : tile.value}
              </p>
            )}
          </div>
        ))}
      </div>
    </article>
  );
}

/** The on-dark delta pill — direction carried by an arrow glyph AND color (a11y: never color alone), tabular. */
function DeltaPill({ value }: { value: number }) {
  const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
  const Glyph = dir === "up" ? ArrowUpRight : dir === "down" ? ArrowDownRight : Minus;
  const rounded = Math.round(value * 10) / 10;
  const sign = rounded > 0 ? "+" : "";
  return (
    <span className={clsx(styles.delta, styles[dir])}>
      <Glyph className={styles.deltaGlyph} aria-hidden="true" />
      {sign}
      {rounded}%
    </span>
  );
}
